// src/components/ReusableChartCard.jsx
import React from "react";
import PropTypes from "prop-types";
import { Card, CardBody } from "../../components/card/card.jsx"; // Adjust path if needed
import Chart from "react-apexcharts"; // Assuming you're using react-apexcharts

const ReusableChartCard = ({
  title,
  totalValue,
  secondaryValue,
  secondaryLabel,
  chartType,
  chartOptions,
  chartSeries,
  footerText,
  footerIconClass,
  height,
  // Props for frequency buttons & limit input
  tabs,
  activeTab,
  onTabChange,
  limit,
  onLimitChange,
}) => {
  // Retrieve your theme color from the CSS variable (set in :root)
  const themeColor = getComputedStyle(document.body)
    .getPropertyValue("--bs-theme")
    .trim();

  // Create a dynamic key for the chart forcing remount if options or series change.
  const chartKey =
    JSON.stringify(chartOptions) + "-" + JSON.stringify(chartSeries);

  // Determine the array of x-axis label colors.
  const categories = chartOptions.xaxis?.categories || [];
  const labelColors = categories.length ? categories.map(() => themeColor) : [themeColor];

  // Create an array for the legend labels. We want them white.
  const legendLabelColors =
    chartSeries.length > 0 ? chartSeries.map(() => themeColor) : ["#fff"];

  // Merge in overrides to set x-axis labels style and legend labels style.
  const modifiedChartOptions = {
    ...chartOptions,
    xaxis: {
      ...chartOptions.xaxis,
      labels: {
        ...chartOptions.xaxis?.labels,
        style: {
          ...chartOptions.xaxis?.labels?.style,
          colors: labelColors,
        },
      },
    },
      yaxis: {
    ...chartOptions.yaxis,
    labels: {
      ...(chartOptions.yaxis?.labels || {}),
      style: {
        ...(chartOptions.yaxis?.labels?.style || {}),
        colors: [themeColor], // <<--- THEME COLOR ON Y AXIS
      },
    },
  },
    legend: {
      ...(chartOptions.legend || {}),
      labels: {
        ...(chartOptions.legend?.labels || {}),
        colors: legendLabelColors,
      },
    },
  };

  // Frequency Button styles
  const activeStyle = {
    // backgroundColor: themeColor,
    border: `1px solid ${themeColor}`,
    color: "#fff",
    padding: "0.5rem 1rem",
    margin: "0.5rem",
    cursor: "pointer",
    borderRadius: "0.25rem",
  };

  const inactiveStyle = {
    backgroundColor: "transparent",
    border: `1px solid ${themeColor}`,
    color: themeColor,
    padding: "0.5rem 1rem",
    margin: "0.5rem",
    cursor: "pointer",
    borderRadius: "0.25rem",
  };

  return (
    <Card className="mb-3 reusable-chart-card" style={{ height: height || "400px" }}>
      <CardBody>
        {/* Header: Title */}
        <div className="d-flex fw-bold small mb-3">
          <span className="flex-grow-1" style={{ fontSize: 15 }}>
            {title}
          </span>
        </div>

        {/* Frequency Buttons & Limit Input */}
{tabs && tabs.length > 0 && (() => {
  const isThemeBlack = document.body.classList.contains('theme-black');

  if (isThemeBlack) {
    // --- BLACK THEME (keep exactly as-is) ---
    return (
      <div className="d-flex flex-wrap justify-content-center align-items-center mb-3 gap-2 theme-black-controlsd-flex flex-wrap justify-content-center align-items-center mb-3 gap-2 theme-black-controls controls-row">
        {tabs.map((tab) => {
          const selected = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              className={`btn freq-btn${selected ? ' active is-active' : ''}`}
              aria-pressed={selected}
              onClick={() => onTabChange(tab.id)}
            >
              {tab.label}
            </button>
          );
        })}

        {onLimitChange && (
          <input
            type="text"
            value={limit}
            onChange={(e) => onLimitChange(e.target.value)}
            placeholder="Limit"
            className="form-control limit-input"
          />
        )}
      </div>
    );
  }

  // --- NON-BLACK THEMES (old style via nav-pills) ---
  return (
    <ul className="nav nav-pills justify-content-center align-items-center mb-3 gap-2 rc-controls" role="tablist">
      {tabs.map((tab) => {
        const selected = activeTab === tab.id;
        return (
          <li key={tab.id} className="nav-item">
            <button
              type="button"
              className={`nav-link${selected ? ' active' : ''}`}
              role="tab"
              aria-selected={selected}
              onClick={() => onTabChange(tab.id)}
            >
              {tab.label}
            </button>
          </li>
        );
      })}
      {onLimitChange && (
        <input
          type="text"
          value={limit}
          onChange={(e) => onLimitChange(e.target.value)}
          placeholder="Limit"
          className="form-control limit-input"
        />
      )}
    </ul>
  );
})()}





        {/* Data Row - Totals in smaller, uniform font */}
        <div className="row align-items-center mb-2">
          <div className="col-6 text-right mb-1">
            <p className="mb-0" style={{ fontSize: "0.9rem" }}>
              Total New Users: {totalValue.toLocaleString()}
            </p>
          </div>
          <div className="col-6 text-end mt-2">
            <p className="mb-0" style={{ fontSize: "0.9rem", color: themeColor}}>
              Total Active Users: {secondaryValue.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Chart Area */}
        <div className="mb-4" style={{ height: "300px", width: "100%" }}>
          <Chart
            key={chartKey} // Forces re-mount when options or series change
            type={chartType}
            height="100%"
            options={modifiedChartOptions}
            series={chartSeries}
          />
        </div>

        {/* Footer */}
        <div className="mt-4 pb-4" style={{ color: "#fff" }}>
          {footerIconClass && <i className={footerIconClass}></i>}
          {footerText}
        </div>
      </CardBody>
    </Card>
  );
};

ReusableChartCard.propTypes = {
  title: PropTypes.string.isRequired,
  totalValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  secondaryValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  secondaryLabel: PropTypes.string,
  chartType: PropTypes.string.isRequired,
  chartOptions: PropTypes.object.isRequired,
  chartSeries: PropTypes.array.isRequired,
  footerText: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  footerIconClass: PropTypes.string,
  height: PropTypes.string,
  // Props for frequency buttons & limit input
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  activeTab: PropTypes.string,
  onTabChange: PropTypes.func,
  limit: PropTypes.string,
  onLimitChange: PropTypes.func,
};

ReusableChartCard.defaultProps = {
  secondaryLabel: "",
  footerIconClass: "",
  height: "400px",
  tabs: [],
  activeTab: "",
  onTabChange: () => {},
  limit: "",
  onLimitChange: () => {},
};

export default ReusableChartCard;
